import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { DropzoneWithFileBrowse, InputReplaceFile } from 'views/components/compound';
import styled from '@emotion/styled';
import { acceptPDF, acceptPDFMimeTypes } from 'utility/constants';
import { extractFileExtentionFromMimeType } from 'utility/helpers';
import { palette, bp, layer } from 'entity/createTheme';
import { UploadPDFMeta, ContentMeta } from 'types/App';
import PDF2Image from 'utility/Pdf2Image';

type Props = {
  editable: boolean;
  meta: UploadPDFMeta;
  handleChangeContentMeta: (meta: ContentMeta) => void;
};

const UploadPDF: FC<Props> = ({ editable, meta, handleChangeContentMeta }) => {
  const { t } = useTranslation();

  const handleChange = async (newFile: any) => {
    const extension = extractFileExtentionFromMimeType(newFile.type);

    if (acceptPDFMimeTypes.includes(extension)) {
      const url = URL.createObjectURL(newFile);
      const pdf2image = await PDF2Image.open(url);

      const thumbnailSrc = await pdf2image.getImageDataUrl(1, {
        width: 400,
        height: 400,
      });

      handleChangeContentMeta({
        ...meta,
        file: newFile,
        filename: newFile.name,
        error: '',
        ...(thumbnailSrc && { thumbnailSrc }),
      });
    }
  };

  return (
    <Root>
      {meta.filename !== '' ? (
        <>
          <Partition />
          <SelectedFileWrapper>
            <ChangeUploadedContentInputWrapper>
              <img src={meta.thumbnailSrc} alt="thumbnail_image" />
              {editable && (
                <InputReplaceFile
                  id="replace_pdf"
                  accept={acceptPDF.join(', ')}
                  className="pdf-replace"
                  handleChange={handleChange}
                />
              )}
            </ChangeUploadedContentInputWrapper>
            <div>
              <FileNameLabel>{t('admin.common.filename')}</FileNameLabel>
              <FileName>{meta.filename}</FileName>
            </div>
          </SelectedFileWrapper>
        </>
      ) : (
        <DropzoneWrapper>
          <DropzoneWithFileBrowse name={meta.filename} onChange={handleChange} type="pdf" />
        </DropzoneWrapper>
      )}
      {meta.error !== '' && <Error className="error">{meta.error}</Error>}
    </Root>
  );
};

const Root = styled('div')({});

const DropzoneWrapper = styled('div')({
  marginTop: 15,
});

const ChangeUploadedContentInputWrapper = styled('div')({
  maxWidth: '40%',
  minWidth: 356,
  height: 200,
  marginRight: 20,
  borderRadius: 6,
  overflow: 'hidden',
  position: 'relative',
  userSelect: 'none',
  background: palette.blackPrimary,

  [`@media ${bp.md}`]: {
    marginBottom: 16,
  },

  img: {
    width: '100%',
    height: '100%',
    objectFit: 'contain',
    '&.not-ready': {
      objectFit: 'cover',
    },
  },

  '.pdf-replace': {
    position: 'absolute',
    top: '10px',
    right: '10px',

    // Might not need to specify z-index
    zIndex: layer.bottomAbove1,
  },
});

const FileNameLabel = styled('div')({
  height: 18,
  color: palette.grayMedium4,
  fontSize: 14,
  letterSpacing: 0,
  lineHeight: '18px',
});

const FileName = styled('div')({
  marginTop: 10,
  color: palette.darkMedium8,
  fontSize: 14,
  letterSpacing: 0,
  lineHeight: 1.5,
  width: '100%',
  overflow: 'hidden',
  display: '-webkit-box',
  WebkitBoxOrient: 'vertical',
  WebkitLineClamp: 8,
  wordBreak: 'break-all',
});

const SelectedFileWrapper = styled('div')({
  display: 'flex',
});

const Partition = styled('div')({
  margin: `15px 0`,
  width: '100%',
  height: 1,
  background: palette.grayPrimary,
});

const Error = styled('p')({
  color: palette.redPrimary,
  marginTop: 20,
  fontSize: 12,
});

export default UploadPDF;
